<template>
  <div>
    <el-row class="title-bar" type="flex" justify="space-between">
      <div class="vertical-center-box">
        <div class="title-h3 mr-r15">[{{ nodeLabel }}] 관리 대상 목록</div>
        <span class="mr-r4">수집여부</span>
        <div class="setting-addel">
          <el-button size="mini" @click="onCollectAll(true)"><i class="fas fa-check-square"></i></el-button>
          <el-button size="mini" @click="onCollectAll(false)"><i class="far fa-square"></i></el-button>
        </div>
      </div>
      <div class="vertical-center-box" align="right">
        <el-button class="mr-r4" size="mini" type="primary" :disabled="!checkDifferenceDataFlag" @click="onSaveButton">저장</el-button>
        <el-button class="mr-r4" size="mini" :disabled="!checkDifferenceDataFlag" @click="onCancelButton">취소</el-button>
        <w-header-search-button @click="toggleSearchTool"/>
      </div>
    </el-row>
    <el-row>
      <el-table-wrapper ref="syslogTable" v-loading="loading" :column-default="columnDefault" :data="targetDatas" :columns="columns" remote :total="total"
      highlight-current-row :show-custom-header="true" :show-search-tool="showSearchTool" @condition-change="onConditionChange"
      @row-dblclick="showRuleTargetInfo" row-class-name="c-dblclick">
        <template slot-scope="scope" slot="check-flag-slot">
          <el-switch v-model="scope.row.rawdataSaveFlagSyslog" active-value="1" inactive-value="0"
          @change="changeDifferenceDataFlag" />
        </template>
      </el-table-wrapper>
    </el-row>
    <w-rule-target-info-popup ref="targetInfoPopup" @load-target-data="loadData"></w-rule-target-info-popup>
  </div>
</template>

<script>
import { isEqual, differenceWith, cloneDeep, isNil, isEmpty, every, find } from 'lodash';

import { EMPTY_TEXT } from '@/utils/string';
import { columnDefault } from '@/constants/table';
import msgUtils from '@/utils/message';
import IntegratedSyslogSettingAPI from '@/api/settings/log/integrated-syslog-settings';

import WRuleTargetInfoPopup from '../popup/RuleTargetInfoPopup';

export default {
  name: 'WSyslogTargetTable',
  components: {
    WRuleTargetInfoPopup,
  },
  props: {
    selectedNode: {
      type: Object,
    },
  },
  data() {
    const columns = [
      {
        label: '수집',
        headerAlign: 'center',
        width: '60px',
        scopedSlot: 'check-flag-slot',
      },
      {
        prop: 'objName',
        label: '장비명',
        searchable: true,
        sortable: true,
        formatter: this.getStringFormatter,
      },
      {
        prop: 'ipaddressIpv4',
        label: 'IP',
        searchable: true,
        sortable: true,
        formatter: this.getStringFormatter,
      },
      {
        prop: 'objtypeName',
        label: '장비종류',
        searchable: true,
        sortable: true,
        formatter: this.getStringFormatter,
      },
    ];
    return {
      loading: false,
      nodeLabel: '네트워크',
      showSearchTool: false,
      targetDatas: [],
      targetOriginDatas: [],
      columns,
      checkedRows: [],
      checkDifferenceDataFlag: false,
      columnDefault,
      collectAll: false,
      selectedRow: {},
      start: 0,
      limit: 25,
      total: 0,
    };
  },
  watch: {
    selectedNode: {
      immediate: true,
      handler(value) {
        if (!isEmpty(value) && value.type !== 'obj') {
          this.nodeLabel = value.label;
        }
        this.$nextTick(() => {
          this.$refs.syslogTable.setCurrentPage(1);
        });
      },
    },
  },
  methods: {
    /**
     * 좌측에서 선택한 노드에 따라 전체 네트워크 장비(root) 또는 objCompanyId별 네트워크 장비 목록을 조회해온다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadTargetData() {
      if (!isNil(this.selectedNode) && !isNil(this.selectedNode.objCompanyId)) {
        this.loadData();
      } else if (!isEmpty(this.selectedNode) && this.selectedNode.type === 'root') {
        this.loadRootData();
      }
    },
    /**
     * 장비의 로그 객체 설정(저장소 설정, 수집서버 설정, 파일 설정) 팝업을 띄운다.
     * @author Minyeong
     * @param  {[type]} data [description]
     * @return {[type]}      [description]
     */
    showRuleTargetInfo(data) {
      const originTarget = find(this.targetOriginDatas, { objId: data.objId });
      this.selectedRow = originTarget;
      this.$refs.targetInfoPopup.open(originTarget);
    },
    /**
     * objCompanyId별 장비 목록을 조회해온다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadData() {
      const searchParam = this.$refs.syslogTable.getParameter();
      let params = {};
      if (this.selectedNode.objCompanyId === '_etc') {
        params = {
          objCompanyId: -1,
          searchParam,
        };
      } else {
        params = {
          objCompanyId: this.selectedNode.objCompanyId,
          searchParam,
        };
      }
      this.loading = true;
      IntegratedSyslogSettingAPI.getSyslogGroupSetList(params).then((response) => {
        this.targetDatas = cloneDeep(response.data.data);
        this.targetOriginDatas = cloneDeep(response.data.data);
        this.total = response.data.total;
        this.$nextTick(() => {
          this.changeDifferenceDataFlag();
        });
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '장비 목록', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    /**
     * 전체 네트워크 장비 목록을 조회해온다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadRootData() {
      const searchParam = this.$refs.syslogTable.getParameter();
      const params = {
        searchParam,
      };
      this.loading = true;
      IntegratedSyslogSettingAPI.getSyslogGroupSetList(params).then((response) => {
        this.targetDatas = cloneDeep(response.data.data);
        this.targetOriginDatas = cloneDeep(response.data.data);
        this.total = response.data.total;
        this.$nextTick(() => {
          this.changeDifferenceDataFlag();
        });
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '장비 목록', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    getStringFormatter(row, column, value) {
      if (!isEmpty(value)) {
        return value;
      }
      return EMPTY_TEXT;
    },
    changeDifferenceDataFlag() {
      this.changeCollectAll();
      this.checkDifferenceDataFlag = !isEqual(this.targetDatas, this.targetOriginDatas);
    },
    onCollectAll(value) {
      if (value) {
        // true : 전체 선택
        this.targetDatas.forEach((item) => {
          item.rawdataSaveFlagSyslog = '1';
        });
      } else {
        // false : 전체 선택 해제
        this.targetDatas.forEach((item) => {
          item.rawdataSaveFlagSyslog = '0';
        });
      }
      this.changeDifferenceDataFlag();
    },
    changeCollectAll() {
      if (isEmpty(this.targetDatas)) {
        this.collectAll = false;
        return;
      }
      if (every(this.targetDatas, { rawdataSaveFlagSyslog: '1' })) {
        this.collectAll = true;
      } else {
        this.collectAll = false;
      }
    },
    onConditionChange(value) {
      this.start = value.start;
      this.limit = value.limit;
      this.loadTargetData();
    },
    /**
     * 장비의 수집 여부를 수정한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    onSaveButton() {
      if (this.loading) {
        return;
      }
      const params = differenceWith(this.targetDatas, this.targetOriginDatas, isEqual);
      this.loading = true;
      IntegratedSyslogSettingAPI.updateSyslogCollectFlag(params).then((response) => {
        if (response.data.success) {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.SAVE, '변경사항', response.data.success));
          this.loadData();
        } else {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.SAVE, '변경사항', response.data.success));
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.SAVE, '변경사항', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    onCancelButton() {
      this.targetDatas = cloneDeep(this.targetOriginDatas);
      this.changeDifferenceDataFlag();
    },
    toggleSearchTool() {
      this.showSearchTool = !this.showSearchTool;
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/settings/log-analysis/Collect/Syslog/components/SyslogTargetTable.vue